/**
 * Rejeu d'une feuille de brouillon enregistrée — piste S de la refonte du moteur.
 *
 * L'app juge chaque ligne pendant le jeu et envoie ses HORA (`record_scratch_line`) :
 * la base ne fait que les borner (12 par ligne, plafond `max_hora` de la question).
 * Ici on REJOUE les lignes enregistrées avec le moteur, comme l'app le fait à la
 * reprise d'une tentative (`ScratchSession.replay`) : chaque ligne est rejugée dans
 * l'ordre (vérification complète), une ligne qui ne se rejuge pas comme elle a été
 * enregistrée n'apprend rien au moteur et ne rapporte rien.
 *
 * L'HORA vérifié d'une ligne = le MINIMUM de ce que l'app a envoyé et de ce que le
 * moteur accorde (`scratchLineGain` sans retenue de calculatrice). On ne peut pas
 * vérifier qu'aucun coup de pouce n'a été demandé ni que la calculatrice n'a pas
 * servi (c'est local à l'appareil) : le drapeau `hinted` est pris tel qu'envoyé, et
 * la retenue de la calculatrice reste incluse dans la valeur envoyée.
 *
 * Pur : aucun réseau. Le même code tourne dans l'app (calcul des gains) et dans
 * l'edge function `scratch-verify` (bundle `bundle-deno.mjs`).
 */

import { StoredScratchModelSchema, isEngineCompatible, ENGINE_VERSION } from './model';
import { commit, createState, judge, type ReasonerState, type Verdict } from './reasoner';
import { chipKind, groupAtoms } from './palette';
import type { Chip } from './lexer';

/** HORA d'une ligne : au plus 12 (une chaîne a = b = c cumule ses maillons), comme la base. */
export const MAX_LINE_HORA = 12;

export interface LineGainOptions {
  /** un coup de pouce a servi pour cette ligne : moitié des HORA (arrondie au-dessus) */
  hinted?: boolean;
  /** HORA retenus parce que la calculatrice a posé un résultat */
  calcCharge?: number;
  /** plafond de la question (`scratch_models.max_hora`) ; null : aucun */
  maxHora?: number | null;
  /** HORA déjà gagnés sur la question avant cette ligne */
  used?: number;
  /**
   * Bonus au geste (clavier v3) : les simplifications faites dans l'éditeur pour écrire cette ligne, déjà
   * bornées par ce que le moteur autorise entre la ligne d'avant et celle-ci. S'ajoute aux HORA du pas,
   * sous les mêmes plafonds — un coup de pouce le réduit de moitié comme le reste.
   */
  bonus?: number;
}

/**
 * HORA d'une ligne juste, justifiée et utile, à partir des HORA du verdict du moteur.
 * Source unique de l'économie de la feuille : l'app (affichage, envoi) et le rejeu serveur.
 */
export function scratchLineGain(raw: number, opts: LineGainOptions = {}): number {
  const base = Math.max(0, raw || 0) + Math.max(0, opts.bonus ?? 0);
  let g = Math.min(MAX_LINE_HORA, opts.hinted ? Math.ceil(base / 2) : base);
  g -= Math.max(0, opts.calcCharge ?? 0);
  if (opts.maxHora != null) g = Math.min(g, Math.max(0, opts.maxHora - (opts.used ?? 0)));
  return Math.max(0, g);
}

/** Une ligne telle qu'enregistrée (`attempt_scratch_lines`). */
export interface StoredScratchRow {
  position: number;
  atoms: string[];
  kind: 'step' | 'kept';
  /** HORA envoyés par l'app (déjà bornés par la base) */
  hora: number;
  hinted: boolean;
  /** simplifications déclarées par l'app pour écrire cette ligne (clavier v3) ; le rejeu les borne */
  simplified?: number;
}

export type ReplayStatus = 'verified' | 'rejected' | 'unchecked';

export interface ReplayedRow {
  position: number;
  /**
   * verified : la ligne se rejuge comme elle a été enregistrée ;
   * rejected : elle ne se rejuge pas (fausse, sans origine, doublon…) → 0 HORA ;
   * unchecked : le moteur ne sait pas lire cette feuille (modèle illisible, version future)
   */
  status: ReplayStatus;
  /** HORA retenus : min(envoyés, accordés par le moteur) ; envoyés si `unchecked` */
  hora: number;
  /** HORA que le moteur accorde à la ligne (sans retenue de calculatrice) */
  bound: number;
  /** genre du verdict du moteur (`error` : le moteur a levé une exception) */
  verdict: string;
  ruleId: string | null;
}

export interface ReplayResult {
  rows: ReplayedRow[];
  /** somme des HORA retenus */
  hora: number;
  /** somme des HORA envoyés */
  claimed: number;
  /** version du moteur qui a rejoué */
  engine: number;
  /** pourquoi la feuille n'a pas été rejouée (lignes `unchecked`) */
  reason?: 'model_unreadable' | 'engine_too_old';
}

export interface ReplayInput {
  /** version du moteur enregistrée avec la feuille (`scratch_models.engine_version`) */
  engineVersion: number;
  model: unknown;
  /** `scratch_models.max_hora` ; null : aucun plafond */
  maxHora: number | null;
  rows: StoredScratchRow[];
}

/** Les bouts d'une ligne enregistrée, comme l'app les reconstruit à la reprise. */
export function storedLineChips(atoms: string[]): Chip[] {
  return groupAtoms(atoms).map((c) => ({
    atoms: c.atoms.slice(),
    text: c.text,
    kind: chipKind(c.atoms),
  }));
}

function judgeRow(st: ReasonerState, chips: Chip[]): Verdict | null {
  try {
    return judge(st, chips, { verify: true });
  } catch {
    return null;
  }
}

function matches(row: StoredScratchRow, v: Verdict | null): boolean {
  if (!v) return false;
  return row.kind === 'kept' ? v.kind === 'useless' : v.kind === 'step';
}

/** Rejoue les lignes d'une feuille (une question, une tentative), dans l'ordre. */
export function replayScratchSheet(input: ReplayInput): ReplayResult {
  const rows = [...input.rows].sort((a, b) => a.position - b.position);
  const claimed = rows.reduce((s, r) => s + Math.max(0, r.hora), 0);
  const unchecked = (reason: ReplayResult['reason']): ReplayResult => ({
    rows: rows.map((r) => ({
      position: r.position,
      status: 'unchecked',
      hora: Math.max(0, r.hora),
      bound: Math.max(0, r.hora),
      verdict: 'unchecked',
      ruleId: null,
    })),
    hora: claimed,
    claimed,
    engine: ENGINE_VERSION,
    reason,
  });

  const parsed = StoredScratchModelSchema.safeParse({
    engine_version: input.engineVersion,
    model: input.model,
  });
  if (!parsed.success) return unchecked('model_unreadable');
  if (!isEngineCompatible(parsed.data.engine_version)) return unchecked('engine_too_old');

  let st: ReasonerState;
  try {
    st = createState(parsed.data.model);
  } catch {
    return unchecked('model_unreadable');
  }

  const out: ReplayedRow[] = [];
  let used = 0;
  for (const row of rows) {
    const chips = storedLineChips(row.atoms);
    let v = judgeRow(st, chips);
    // une seconde chance : un budget de temps épuisé (machine chargée) ne doit pas
    // faire perdre une ligne juste
    if (!matches(row, v)) v = judgeRow(st, chips) ?? v;
    if (!v || !matches(row, v)) {
      out.push({
        position: row.position,
        status: 'rejected',
        hora: 0,
        bound: 0,
        verdict: v?.kind ?? 'error',
        ruleId: null,
      });
      continue;
    }
    commit(st, v);
    const bound =
      row.kind === 'kept'
        ? 0
        : scratchLineGain(v.hora ?? 0, { hinted: row.hinted, maxHora: input.maxHora, used });
    const hora = Math.min(Math.max(0, row.hora), bound);
    used += hora;
    out.push({
      position: row.position,
      status: 'verified',
      hora,
      bound,
      verdict: v.kind,
      ruleId: v.rule?.id ?? null,
    });
  }
  return {
    rows: out,
    hora: out.reduce((s, r) => s + r.hora, 0),
    claimed,
    engine: ENGINE_VERSION,
  };
}
